import React, { MouseEvent } from 'react'
import { compact } from 'lodash'
import { TreeItem } from '@mui/lab'

import { ObjectLiteral } from 'interfaces'

import { ReactComponent as ExpandMoreIcon } from 'assets/icons/arrow_right.svg'
import { ReactComponent as CollapseIcon } from 'assets/icons/arrow_down.svg'
import { Icon } from '@ketch-com/deck'
import { Box, Theme } from '@mui/material'

interface Props<T> {
  item: T
  classes: ObjectLiteral
  onItemClick: (item: T) => void
  itemRenderer: (item: T, searchString?: string) => React.ReactNode
  type: string
  searchString?: string
  expandableKey?: string
  expanded: string[]
  theme: Theme
}

export function constructListItem<T extends ObjectLiteral>({
  item,
  classes,
  onItemClick,
  itemRenderer,
  type,
  searchString,
  expandableKey,
  expanded,
  theme,
}: Props<T>) {
  const onClick = (e: MouseEvent<HTMLDivElement>) => {
    e.stopPropagation()
    onItemClick(item)
  }
  const isExpandable = !(!expandableKey || !item[expandableKey])

  const shouldShowExpand = expanded.includes(item.code)

  return (
    <TreeItem
      key={`${item.code}-${item.ID}`}
      nodeId={item.code}
      label={
        <div
          onClick={e => {
            !isExpandable && onClick(e)
          }}
          className={classes.listItem}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            onClick={onClick}
          >
            {type === 'INCLUDE' ? (
              <Icon name="FMinusRound" iconColor={theme.palette.Text.Secondary} />
            ) : (
              <Icon name="FPlusRound" iconColor={theme.palette.Text.Secondary} />
            )}
          </Box>
          {isExpandable && (
            <div className={classes.iconExpandWrapper}>{!shouldShowExpand ? <ExpandMoreIcon /> : <CollapseIcon />}</div>
          )}
          <div className={classes.itemContent}>{itemRenderer(item, type)}</div>
        </div>
      }
      classes={{
        iconContainer: classes.iconContainer,
        content: classes.treeItem,
        label: classes.label,
      }}
    >
      {expandableKey && item[expandableKey]
        ? item[expandableKey].map((item: T) =>
            constructListItem({
              item,
              classes,
              onItemClick,
              itemRenderer,
              type,
              searchString,
              expandableKey,
              expanded,
              theme,
            }),
          )
        : null}
    </TreeItem>
  )
}

interface FilterTreeItemProps<T> {
  item: T
  search: string
  expandableKey?: string
}

export function filterTreeItem<T extends ObjectLiteral>({
  item,
  search,
  expandableKey = 'regions',
}: FilterTreeItemProps<T>) {
  const isItemMatched = item.name.toLowerCase().indexOf(search.toLowerCase()) !== -1
  const currentLevelItemFiltered = isItemMatched ? item : null

  if (item[expandableKey]) {
    const childItemsFiltered = compact(
      item[expandableKey].map((item: T) => filterTreeItem({ item, search, expandableKey })),
    )

    return childItemsFiltered.length
      ? {
          ...item,
          [expandableKey]: childItemsFiltered,
        }
      : currentLevelItemFiltered
  } else {
    return currentLevelItemFiltered
  }
}
